import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, Building, Clock, Calendar, MessageSquare, ExternalLink, Edit, Send } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { api } from '../../lib/api';
import { fmtDate, fmtTime, fmtDay, StatusBadge } from '../../lib/format';
import { toast } from 'sonner';

const STATUSES = ['pending', 'confirmed', 'rescheduled', 'completed', 'cancelled', 'no_show'];

export default function AdminAppointmentDetailPage() {
  const { id } = useParams();
  const [appt, setAppt] = useState(null);
  const [advisors, setAdvisors] = useState([]);
  const [note, setNote] = useState('');
  const [meetingLink, setMeetingLink] = useState('');

  const load = () => api.getAppointment(id).then((a) => {
    setAppt(a);
    setMeetingLink(a.meeting_link || '');
  });

  useEffect(() => {
    load();
    api.adminAdvisors().then(setAdvisors);
    // eslint-disable-next-line
  }, [id]);

  if (!appt) return <AdminLayout title="Appointment"><div className="text-neutral-400">Loading…</div></AdminLayout>;

  const advisor = advisors.find((a) => a.id === appt.advisor_id);

  const updateStatus = async (status) => {
    try {
      await api.updateStatus(appt.id, { status, meeting_link: meetingLink });
      toast.success(`Status updated to ${status}`);
      load();
    } catch (e) { toast.error('Could not update status'); }
  };

  const saveLink = async () => {
    try {
      await api.updateStatus(appt.id, { status: appt.status, meeting_link: meetingLink });
      toast.success('Meeting link saved');
      load();
    } catch { toast.error('Save failed'); }
  };

  const submitNote = async () => {
    if (!note.trim()) return;
    try {
      await api.addNote(appt.id, note.trim());
      setNote('');
      toast.success('Note added');
      load();
    } catch { toast.error('Could not add note'); }
  };

  return (
    <AdminLayout title="Appointment" subtitle={appt.appointment_type_name}
      actions={<Link to="/admin/appointments" className="inline-flex items-center gap-1.5 text-[13px] text-neutral-500 hover:text-[#c89200]"><ArrowLeft className="w-4 h-4" /> Back</Link>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-xl bg-white border border-neutral-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[17px] font-bold text-[#14110f]">Details</h3>
              <StatusBadge status={appt.status} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Info icon={Calendar} label="Date" value={fmtDay(appt.start)} />
              <Info icon={Clock} label="Time" value={`${fmtTime(appt.start)} · ${appt.duration_minutes} min`} />
              <Info icon={ExternalLink} label="Location" value={appt.location_type.replace('_', ' ')} />
              <Info icon={Building} label="Advisor" value={advisor?.name || '—'} />
            </div>
            <div className="mt-5 pt-5 border-t border-neutral-100">
              <label className="text-[11px] uppercase tracking-wider text-neutral-500">Meeting link</label>
              <div className="mt-2 flex gap-2">
                <input value={meetingLink} onChange={(e) => setMeetingLink(e.target.value)} placeholder="https://zoom.us/j/..." className="flex-1 px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none" />
                <button onClick={saveLink} className="px-4 py-2.5 rounded-md bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] text-[13px] font-semibold">Save</button>
              </div>
            </div>
            {appt.notes && (
              <div className="mt-5 pt-5 border-t border-neutral-100">
                <p className="text-[11px] uppercase tracking-wider text-neutral-500">Client message</p>
                <p className="mt-1.5 text-[13.5px] text-neutral-700">{appt.notes}</p>
              </div>
            )}
            {Object.keys(appt.intake_answers || {}).length > 0 && (
              <div className="mt-5 pt-5 border-t border-neutral-100 space-y-2">
                <p className="text-[11px] uppercase tracking-wider text-neutral-500">Intake answers</p>
                {Object.entries(appt.intake_answers).map(([q, a]) => (
                  <div key={q}>
                    <p className="text-[12.5px] font-semibold text-[#14110f]">{q}</p>
                    <p className="text-[13px] text-neutral-600">{a}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-xl bg-white border border-neutral-100 p-6">
            <h3 className="text-[17px] font-bold text-[#14110f] mb-4">Internal notes</h3>
            <div className="flex gap-2">
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add a note visible to admins/advisors" className="flex-1 px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none resize-none" />
              <button onClick={submitNote} className="px-4 rounded-md bg-[#14110f] text-white text-[12.5px] font-semibold hover:bg-[#2a2622] flex items-center gap-1.5"><Send className="w-3.5 h-3.5" /> Add</button>
            </div>
            <ul className="mt-5 space-y-3">
              {(appt.notes_list || []).length === 0 && <p className="text-[12.5px] text-neutral-400 italic">No notes yet.</p>}
              {(appt.notes_list || []).map((n) => (
                <li key={n.id} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#f5b300]/15 flex items-center justify-center shrink-0"><MessageSquare className="w-4 h-4 text-[#c89200]" /></div>
                  <div className="flex-1">
                    <p className="text-[13.5px] text-neutral-700">{n.body}</p>
                    <p className="text-[11px] text-neutral-400 mt-1">{n.author_name} · {fmtDate(n.created_at)}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-xl bg-white border border-neutral-100 p-6">
            <h3 className="text-[17px] font-bold text-[#14110f] mb-4">Client</h3>
            <p className="text-[16px] font-semibold text-[#14110f]">{appt.client_first_name} {appt.client_last_name}</p>
            {appt.client_company && <p className="text-[13px] text-neutral-500">{appt.client_company}</p>}
            <div className="mt-4 space-y-2.5 text-[13px]">
              <p className="flex items-center gap-2 text-neutral-700"><Mail className="w-4 h-4 text-[#c89200]" /> <a href={`mailto:${appt.client_email}`} className="hover:underline">{appt.client_email}</a></p>
              {appt.client_phone && <p className="flex items-center gap-2 text-neutral-700"><Phone className="w-4 h-4 text-[#c89200]" /> {appt.client_phone}</p>}
            </div>
            {appt.client_id && <Link to={`/admin/clients/${appt.client_id}`} className="mt-4 inline-flex items-center gap-1.5 text-[12.5px] text-[#c89200] font-semibold hover:underline">View client profile <ExternalLink className="w-3.5 h-3.5" /></Link>}
          </div>

          <div className="rounded-xl bg-white border border-neutral-100 p-6">
            <h3 className="text-[17px] font-bold text-[#14110f] mb-4">Update status</h3>
            <div className="grid grid-cols-2 gap-2">
              {STATUSES.map((s) => (
                <button key={s} onClick={() => updateStatus(s)} className={`px-3 py-2.5 rounded-md text-[12.5px] font-semibold border transition ${appt.status === s ? 'bg-[#f5b300] border-[#f5b300] text-[#14110f]' : 'border-neutral-200 hover:border-[#f5b300] hover:text-[#c89200]'}`}>{s.replace('_', '-')}</button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 p-3.5 rounded-lg bg-[#faf6ec]">
      <Icon className="w-4.5 h-4.5 text-[#c89200] mt-0.5" />
      <div>
        <p className="text-[10.5px] uppercase tracking-wider text-neutral-500">{label}</p>
        <p className="font-semibold text-[13.5px] text-[#14110f] capitalize">{value}</p>
      </div>
    </div>
  );
}
